import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CurrentUser,
  JwtPayload,
  LoginCredentials,
  LoginResponse,
  OffersResponse,
  OrganisationRequestReceipt,
  OrganisationRequestStatusResponse,
  RegisterOrganisationInput,
  PublicLegalContent,
  PublicConfiguration,
  TestimonialsResponse,
  HomeVideosResponse,
  PayTechPaymentMethod,
  RegistrationPayment,
} from '../models/auth.model';
import { TokenRefreshService } from './token-refresh.service';
import { TokenStorageService } from './token-storage.service';
import { LanguageService } from '../../i18n/language.service';
import { AppLanguage } from '../../i18n/translations';

/**
 * Point d'entrée unique pour tout ce qui touche à la session utilisateur.
 * L'état courant est exposé via des signaux pour une consommation réactive
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly tokenRefresh = inject(TokenRefreshService);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);

  private readonly currentUserSignal = signal<CurrentUser | null>(this.readUserFromStoredToken());

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUserSignal() !== null);
  readonly subscriptionReadOnly = computed(
    () => this.currentUserSignal()?.subscriptionAccessMode === 'read_only',
  );

  constructor() {
    this.tokenStorage.cleared$.subscribe(() => this.currentUserSignal.set(null));
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    const normalizedCredentials: LoginCredentials = {
      ...credentials,
      email: credentials.email.trim().toLowerCase(),
    };

    return this.http.post<LoginResponse>(`${environment.apiUrl}/login`, normalizedCredentials, { withCredentials: true }).pipe(
      tap((response) => this.applyToken(response.token)),
      catchError((error) => throwError(() => error)),
    );
  }


  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiUrl}/public/mot-de-passe/oubli`,
      { email: email.trim().toLowerCase() },
    );
  }

  resetPassword(payload: {
    token: string;
    nouveauMotDePasse: string;
    confirmationMotDePasse: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiUrl}/public/mot-de-passe/reinitialiser`,
      payload,
    );
  }

  getPublicConfiguration(): Observable<PublicConfiguration> {
    return this.http.get<PublicConfiguration>(`${environment.apiUrl}/public/configuration`);
  }

  getOffers(): Observable<OffersResponse> {
    return this.http.get<OffersResponse>(`${environment.apiUrl}/public/offres`);
  }

  getTestimonials(): Observable<TestimonialsResponse> {
    return this.http.get<TestimonialsResponse>(`${environment.apiUrl}/public/temoignages`);
  }

  getHomeVideos(): Observable<HomeVideosResponse> {
    return this.http.get<HomeVideosResponse>(`${environment.apiUrl}/public/videos-accueil`);
  }

  registerOrganisation(payload: RegisterOrganisationInput): Observable<OrganisationRequestReceipt> {
    return this.http.post<OrganisationRequestReceipt>(
      `${environment.apiUrl}/public/demandes-organisations`,
      {
        ...payload,
        email: payload.email.trim().toLowerCase(),
      },
    );
  }

  getOrganisationRequestStatus(reference: string, suiviToken: string): Observable<OrganisationRequestStatusResponse> {
    return this.http.get<OrganisationRequestStatusResponse>(
      `${environment.apiUrl}/public/demandes-organisations/${reference}/statut`,
      { params: { token: suiviToken } },
    );
  }

  retryOrganisationPayment(reference: string, suiviToken: string, methodePaiement: PayTechPaymentMethod): Observable<{ paiement: RegistrationPayment }> {
    return this.http.post<{ paiement: RegistrationPayment }>(
      `${environment.apiUrl}/public/demandes-organisations/${reference}/paiements/reessayer`,
      { methodePaiement, suiviToken },
    );
  }

  reportOrganisationPaymentCancellation(reference: string, suiviToken: string): Observable<{ paiement: RegistrationPayment | null }> {
    return this.http.post<{ paiement: RegistrationPayment | null }>(
      `${environment.apiUrl}/public/demandes-organisations/${reference}/paiements/retour-annulation`,
      { suiviToken },
    );
  }


  getPublicContent(slug: string): Observable<PublicLegalContent> {
    return this.http.get<PublicLegalContent>(`${environment.apiUrl}/public/contenus/${encodeURIComponent(slug)}`);
  }

  updateLanguage(_langue: AppLanguage): Observable<{ langue: AppLanguage; message: string }> {
    const langue: AppLanguage = 'fr';
    const previous = this.languageService.language();
    this.languageService.setLanguage(langue);

    return this.http.patch<{ langue: AppLanguage; message: string }>(
      `${environment.apiUrl}/compte/langue`,
      { langue },
      { withCredentials: true },
    ).pipe(
      tap((response) => {
        this.languageService.setFromAccount(response.langue);
        const user = this.currentUserSignal();
        if (user) {
          this.currentUserSignal.set({ ...user, langue: response.langue });
        }
      }),
      catchError((error) => {
        this.languageService.setLanguage(previous);
        return throwError(() => error);
      }),
    );
  }

  changePassword(payload: {
    motDePasseActuel: string;
    nouveauMotDePasse: string;
    confirmationMotDePasse: string;
  }): Observable<{ message: string; deconnexionRequise: boolean }> {
    return this.http.patch<{ message: string; deconnexionRequise: boolean }>(
      `${environment.apiUrl}/compte/mot-de-passe`,
      payload,
      { withCredentials: true },
    );
  }

  logout(): void {
    this.tokenRefresh.revokeRefreshToken().subscribe({
      complete: () => this.clearLocalSession(),
      error: () => this.clearLocalSession(),
    });
  }

  ensureFreshSession(minValiditySeconds = 45): Observable<boolean> {
    const token = this.tokenStorage.getToken();
    if (token && !this.tokenStorage.isExpired(token, minValiditySeconds)) {
      return of(true);
    }

    return this.refreshSession({ preserveUsableToken: true });
  }

  refreshSession(options: { preserveUsableToken?: boolean } = {}): Observable<boolean> {
    const tokenBeforeRefresh = this.tokenStorage.getToken();

    return this.tokenRefresh.refreshAccessToken().pipe(
      tap((token) => this.applyToken(token)),
      map(() => true),
      catchError(() => {
        const currentToken = this.tokenStorage.getToken();

        // Le guestGuard peut lancer un refresh dès l'ouverture de /connexion.
        // Si l'utilisateur se connecte pendant que cette ancienne requête est
        // encore en vol, son échec ne doit jamais effacer le JWT fraîchement
        // reçu par le formulaire de connexion.
        if (
          currentToken &&
          currentToken !== tokenBeforeRefresh &&
          !this.tokenStorage.isExpired(currentToken)
        ) {
          return of(true);
        }

        // Un refresh préventif peut échouer momentanément alors que le JWT
        // courant est encore utilisable. On conserve alors la session au lieu
        // de déconnecter l'utilisateur avant même le premier appel de caisse.
        if (
          options.preserveUsableToken &&
          currentToken &&
          !this.tokenStorage.isExpired(currentToken, 0)
        ) {
          return of(true);
        }

        this.tokenStorage.clear();
        this.currentUserSignal.set(null);
        return of(false);
      }),
    );
  }

  getToken(): string | null {
    return this.tokenStorage.getToken();
  }

  updateOrganisationCurrency(devise: string): void {
    const user = this.currentUserSignal();
    const normalized = devise.trim().toUpperCase();
    if (!user || !/^[A-Z]{3}$/.test(normalized)) {
      return;
    }

    localStorage.setItem(this.currencyStorageKey(user.organisationId), normalized);
    this.currentUserSignal.set({ ...user, devise: normalized });
  }

  hasPermission(permission: string): boolean {
  const user = this.currentUserSignal();

  if (!user) {
    return false;
  }

  const roles = this.normalizeStringArray(user.roles);
  const permissions = this.normalizeStringArray(user.permissions);

  return (
    roles.includes('ROLE_ADMIN')
    || roles.includes('ROLE_SUPER_ADMIN')
    || permissions.includes('*')
    || permissions.includes(permission)
  );
}

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasAnyRole(roles: string[]): boolean {
  const user = this.currentUserSignal();

  if (!user) {
    return false;
  }

  const currentRoles = this.normalizeStringArray(user.roles);

  return roles.some((role) => currentRoles.includes(role));
}

  /** Vérifie l'expiration du token courant (utilisé par le guard et l'intercepteur). */
  isTokenValid(): boolean {
    const token = this.tokenStorage.getToken();
    if (!token) {
      return false;
    }
    try {
      const payload = jwtDecode<JwtPayload>(token);
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  private clearLocalSession(): void {
    this.tokenStorage.clear();
    this.currentUserSignal.set(null);
    void this.router.navigate(['/connexion']);
  }

  private applyToken(token: string): void {
    this.tokenRefresh.adoptAccessToken(token);
    const user = this.mapPayloadToUser(jwtDecode<JwtPayload>(token));
    this.languageService.setFromAccount(user.langue);
    this.currentUserSignal.set(user);
  }

  private readUserFromStoredToken(): CurrentUser | null {
    const token = this.tokenStorage.getToken();
    if (!token) {
      return null;
    }
    try {
      const payload = jwtDecode<JwtPayload>(token);
      if (payload.exp * 1000 <= Date.now()) {
        this.tokenStorage.clear();
        return null;
      }
      const mappedUser = this.mapPayloadToUser(payload);
      // Le choix local peut être plus récent que le JWT courant : l'endpoint
      // de préférence ne réémet pas inutilement un jeton à chaque changement.
      const user = { ...mappedUser, langue: this.languageService.language() };
      this.languageService.setFromAccount(user.langue);
      return user;
    } catch {
      this.tokenStorage.clear();
      return null;
    }
  }

 private mapPayloadToUser(payload: JwtPayload): CurrentUser {
  return {
    id: payload.user_id,
    email: payload.email,
    nom: payload.nom,
    organisationId: payload.organisation_id,
    organisationSlug: payload.organisation_slug,
    organisationNom: payload.organisation_nom,
    devise:
      this.readStoredCurrency(payload.organisation_id)
      ?? payload.devise
      ?? 'XOF',
    plan: payload.plan,
    permissions: this.normalizeStringArray(payload.permissions),
    roles: this.normalizeStringArray(payload.roles),
    features: this.normalizeBooleanRecord(payload.features),
    limits: this.normalizeNumberRecord(payload.limits),
    subscriptionAccessMode: payload.subscription_access_mode === 'read_only' ? 'read_only' : 'full',
    subscriptionExpired: payload.subscription_expired === true,
    subscriptionEndsAt: payload.subscription_ends_at ?? null,
    depotId: payload.depot_id ?? null,
    depotNom: payload.depot_nom ?? null,
    forcePasswordChange: payload.force_password_change ?? false,
    deuxFacteurs: payload.deux_facteurs,
    langue: 'fr',
  };
}

  private readStoredCurrency(organisationId: string): string | null {
    const value = localStorage.getItem(this.currencyStorageKey(organisationId));
    return value && /^[A-Z]{3}$/.test(value) ? value : null;
  }

  private currencyStorageKey(organisationId: string): string {
    return `lem:currency:${organisationId}`;
  }

  private normalizeBooleanRecord(value: unknown): Record<string, boolean> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
    );
  }

  private normalizeNumberRecord(value: unknown): Record<string, number | null> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    const result: Record<string, number | null> = {};
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      if (raw === null) result[key] = null;
      else if (typeof raw === 'number' && Number.isFinite(raw)) result[key] = raw;
    }
    return result;
  }

  private normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return [...new Set(
      value.filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean),
    )];
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return [];
    }

    // Le backend peut avoir envoyé un tableau encodé en JSON.
    try {
      return this.normalizeStringArray(JSON.parse(trimmedValue));
    } catch {
      return [trimmedValue];
    }
  }

  if (value && typeof value === 'object') {
    const permissions: string[] = [];

    for (const [key, nestedValue] of Object.entries(
      value as Record<string, unknown>,
    )) {
      // Exemple : { "PRODUIT_LIRE": true }
      if (nestedValue === true) {
        permissions.push(key);
        continue;
      }

      // Exemple : { "catalogue": ["PRODUIT_LIRE", "PRODUIT_MODIFIER"] }
      permissions.push(...this.normalizeStringArray(nestedValue));
    }

    return [...new Set(permissions)];
  }

  return [];
}
}
